import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SavedItemType } from "@corianpark/shared";
import { api } from "../lib/api.js";
import { useAuth } from "./useAuth.js";

export interface HydratedSavedItem {
  id: string;
  userId: string;
  itemType: SavedItemType;
  itemId: string;
  note: string | null;
  createdAt: string;
  hydrated: {
    title: string;
    slug: string | null;
    thumbnailUrl: string | null;
    meta?: Record<string, unknown>;
  } | null;
}

interface SavedListResponse {
  items: HydratedSavedItem[];
}

interface SavedItem {
  id: string;
  userId: string;
  itemType: SavedItemType;
  itemId: string;
  note: string | null;
  createdAt: string;
}

export function useSavedList(itemType?: SavedItemType) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["saved", "list", user?.id, itemType ?? "all"],
    queryFn: () =>
      api<SavedListResponse>(`/v1/saved${itemType ? `?itemType=${itemType}` : ""}`),
    enabled: !!user,
  });
}

export function useSaveItem() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: (input: { itemType: SavedItemType; itemId: string; note?: string }) =>
      api<SavedItem>("/v1/saved", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["saved", "list", user?.id] });
    },
  });
}

export function useUnsaveItem() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/v1/saved/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["saved", "list", user?.id] });
    },
  });
}

export function useUpdateSavedNote() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: (input: { id: string; note: string | null }) =>
      api<SavedItem>(`/v1/saved/${input.id}`, {
        method: "PATCH",
        body: JSON.stringify({ note: input.note }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["saved", "list", user?.id] });
    },
  });
}
